import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Search, Pencil, Trash2, Users, Star } from 'lucide-react';
import SEO from '@/components/SEO';

type ThriveCircleRow = {
  id: string;
  name: string | null;
  description: string | null;
  category: string | null;
  member_count: number | null;
  privacy_level: string | null;
  is_featured: boolean | null;
  subscriptionPlan: string;
  
  createdAt: string;
};

export default function CommunitiesPage() {
  const [communities, setCommunities] = useState<ThriveCircleRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editDialog, setEditDialog] = useState<{ open: boolean; community: ThriveCircleRow | null }>({ open: false, community: null });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; community: ThriveCircleRow | null }>({ open: false, community: null });
  const [editForm, setEditForm] = useState({ name: '', description: '', category: '', privacy_level: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => { fetchCommunities(); }, []);

  const fetchCommunities = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('ThriveCircle')
      .select('id, name, description, category, member_count, privacy_level, is_featured, subscriptionPlan, createdAt')
      .order('createdAt', { ascending: false });
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else setCommunities(data || []);
    setIsLoading(false);
  };

  const toggleFeatured = async (id: string, current: boolean | null) => {
    const { error } = await supabase.from('ThriveCircle').update({ is_featured: !current, updatedAt: new Date().toISOString() }).eq('id', id);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { setCommunities(prev => prev.map(c => c.id === id ? { ...c, is_featured: !current } : c)); }
  };

  const openEdit = (c: ThriveCircleRow) => {
    setEditForm({ name: c.name || '', description: c.description || '', category: c.category || '', privacy_level: c.privacy_level || '' });
    setEditDialog({ open: true, community: c });
  };

  const saveEdit = async () => {
    if (!editDialog.community) return;
    setIsSubmitting(true);
    const { error } = await supabase.from('ThriveCircle').update({
      name: editForm.name, description: editForm.description, category: editForm.category || null,
      privacy_level: editForm.privacy_level || null, updatedAt: new Date().toISOString(),
    }).eq('id', editDialog.community.id);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Updated' }); fetchCommunities(); }
    setEditDialog({ open: false, community: null });
    setIsSubmitting(false);
  };

  const confirmDelete = async () => {
    if (!deleteDialog.community) return;
    setIsSubmitting(true);
    const { error } = await supabase.from('ThriveCircle').delete().eq('id', deleteDialog.community.id);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Deleted' }); fetchCommunities(); }
    setDeleteDialog({ open: false, community: null });
    setIsSubmitting(false);
  };

  const filtered = communities.filter(c =>
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.category || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <SEO title="Communities" noindex />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Communities</h1>
          <p className="text-muted-foreground mt-1">Manage all ThriveCircle communities</p>
        </div>

        <Card>
          <CardHeader>
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search communities..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No communities found.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Members</TableHead>
                    <TableHead>Privacy</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Featured</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(c => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.name || '—'}</TableCell>
                      <TableCell>{c.category ? <Badge variant="secondary">{c.category}</Badge> : '—'}</TableCell>
                      <TableCell>{c.member_count ?? 0}</TableCell>
                      <TableCell>{c.privacy_level || '—'}</TableCell>
                      <TableCell><Badge variant="outline">{c.subscriptionPlan}</Badge></TableCell>
                      <TableCell>
                        <Switch checked={!!c.is_featured} onCheckedChange={() => toggleFeatured(c.id, c.is_featured)} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/communities/members/${c.id}`)}>
                            <Users className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/communities/posts/${c.id}`)}>
                            Posts
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(c)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDeleteDialog({ open: true, community: c })}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={open => !open && setEditDialog({ open: false, community: null })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Community</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div><Label>Description</Label><Textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} rows={3} /></div>
            <div><Label>Category</Label><Input value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))} /></div>
            <div><Label>Privacy Level</Label><Input value={editForm.privacy_level} onChange={e => setEditForm(f => ({ ...f, privacy_level: e.target.value }))} placeholder="public / private" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, community: null })}>Cancel</Button>
            <Button onClick={saveEdit} disabled={isSubmitting}>{isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog.open} onOpenChange={open => !open && setDeleteDialog({ open: false, community: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Community</DialogTitle>
            <DialogDescription>This will permanently delete "{deleteDialog.community?.name}" and cascade to all members and posts. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, community: null })}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
